import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ChevronLeft, Send, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function ConversationPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [bookingId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchData = async () => {
    try {
      const [messagesRes, bookingRes] = await Promise.all([
        api.get(`/messages/${bookingId}`),
        api.get(`/bookings/${bookingId}`),
      ]);
      setMessages(messagesRes.data || []);
      setBooking(bookingRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/messages/${bookingId}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await api.post("/messages", {
        booking_id: bookingId,
        content: newMessage.trim(),
      });
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  const otherUser = booking?.guest_id === user?.user_id ? booking?.host : booking?.guest;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col" data-testid="conversation-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10">
        <div className="container-main py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/inbox")}
              className="p-2 hover:bg-sand rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <Avatar className="w-10 h-10">
              <AvatarImage src={otherUser?.picture} />
              <AvatarFallback className="bg-terracotta text-white">
                {otherUser?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <p className="font-semibold">{otherUser?.name}</p>
              <p className="text-sm text-gray-500">
                {booking?.vehicle?.make} {booking?.vehicle?.model}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="container-main">
          {messages.map((msg, index) => {
            const isMine = msg.sender_id === user?.user_id;
            const showDate =
              index === 0 ||
              new Date(msg.created_at).toDateString() !==
                new Date(messages[index - 1].created_at).toDateString();

            return (
              <div key={msg.message_id}>
                {showDate && (
                  <div className="text-center my-4">
                    <span className="text-xs text-gray-500 bg-sand px-3 py-1 rounded-full">
                      {format(new Date(msg.created_at), "EEEE, d MMMM", { locale: es })}
                    </span>
                  </div>
                )}
                
                <div className={`flex ${isMine ? "justify-end" : "justify-start"} mb-3`}>
                  <div
                    className={`max-w-[75%] px-4 py-2 ${
                      isMine ? "chat-bubble-sent" : "chat-bubble-received"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p
                      className={`text-xs mt-1 ${
                        isMine ? "text-white/70" : "text-gray-400"
                      }`}
                    >
                      {format(new Date(msg.created_at), "HH:mm")}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-[#E5E0D8] p-4">
        <form onSubmit={handleSend} className="container-main flex gap-3">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Escribe un mensaje..."
            className="flex-1 input-base"
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-terracotta hover:bg-terracotta-hover"
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
